import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BarChart3, Plus, Trash2 } from 'lucide-react';
import {
  ROTULO_STATUS,
  statusEfetivo,
  transicoesDe,
  type StatusCotacao,
} from '@profitlens/dominio';
import { Cabecalho } from '../componentes/Cabecalho';
import { Carregando, Erro, Vazio } from '../componentes/Estados';
import { Campo, CampoInteiro, CampoQuantidade, CampoReais, Texto as CampoTexto } from '../componentes/Campos';
import { SeloCotacao } from '../componentes/SeloCotacao';
import { TabelaComparativo, type OrigemLinha } from '../componentes/TabelaComparativo';
import { Quantidade } from '../componentes/Valor';
import { useAuth } from '../contextos/AuthContext';
import { useFornecedores } from '../servicos/fornecedores';
import { useProdutos } from '../servicos/produtos';
import { useUsuarios } from '../servicos/usuarios';
import {
  adicionarItem,
  cotacaoVazia,
  criarCotacao,
  hojeISO,
  removerItem,
  salvarCotacao,
  salvarResposta,
  transitarStatus,
  useCotacao,
  useItens,
  useRespostas,
  useStatusHist,
  type DadosResposta,
  type RascunhoCotacao,
} from '../servicos/cotacoes';
import type { ItemCotacao, RespostaCotacao } from '../tipos';

export function CotacaoFicha() {
  const { id } = useParams<{ id: string }>();
  const nova = id === 'nova';
  const cotacaoId = nova ? null : (id ?? null);

  const navegar = useNavigate();
  const { perfil, podeEscrever } = useAuth();
  const { cotacao, carregando } = useCotacao(cotacaoId);
  const { fornecedores } = useFornecedores();
  const { produtos } = useProdutos();
  const { usuarios } = useUsuarios();
  const itens = useItens(cotacaoId);
  const respostas = useRespostas(cotacaoId);
  const historico = useStatusHist(cotacaoId);

  const [rascunho, setRascunho] = useState<RascunhoCotacao>(cotacaoVazia(perfil?.id ?? ''));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!cotacao) return;
    const { id: _i, referencia: _r, status: _s, criadoEm, criadoPor, atualizadoEm, atualizadoPor, ...resto } =
      cotacao;
    setRascunho(resto);
  }, [cotacao?.id]);

  const porId = useMemo(() => Object.fromEntries(produtos.map((p) => [p.id, p])), [produtos]);
  const nomesUsuarios = useMemo(
    () => Object.fromEntries(usuarios.map((u) => [u.id, u.nome])),
    [usuarios],
  );
  const respostaDoItem = useMemo(
    () => Object.fromEntries(respostas.map((r) => [r.itemId, r])) as Record<string, RespostaCotacao>,
    [respostas],
  );

  if (!nova && carregando) return <Carregando />;
  if (!nova && !cotacao) {
    return <Erro titulo="Cotação não encontrada" detalhe="Ela pode ter sido removida." />;
  }

  const fornecedor = cotacao ? fornecedores.find((f) => f.id === cotacao.fornecedorId) : undefined;
  const efetivo: StatusCotacao = cotacao
    ? statusEfetivo(cotacao.status, cotacao.validade, hojeISO())
    : 'RASCUNHO';

  /**
   * As transicoes partem do status GRAVADO, nao do efetivo. Uma cotacao exibida
   * como expirada continua sendo, no banco, uma ENVIADA — e e dela que saem os
   * caminhos possiveis (RN-040/041).
   */
  const proximos = cotacao ? transicoesDe(cotacao.status) : [];

  const editar = <K extends keyof RascunhoCotacao>(campo: K, valor: RascunhoCotacao[K]) =>
    setRascunho((r) => ({ ...r, [campo]: valor }));

  async function salvar(e: FormEvent) {
    e.preventDefault();
    if (!perfil) return;
    if (!rascunho.fornecedorId) {
      setErro('Escolha o fornecedor: uma cotação é sempre de alguém.');
      return;
    }
    setErro(null);
    setSalvando(true);
    try {
      if (nova) {
        const novoId = await criarCotacao(rascunho, perfil.id);
        navegar(`/cotacoes/${novoId}`, { replace: true });
      } else {
        await salvarCotacao(cotacaoId!, rascunho, perfil.id);
      }
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  }

  async function mudarStatus(para: StatusCotacao) {
    if (!perfil || !cotacao) return;
    setErro(null);
    try {
      await transitarStatus(cotacao.id, cotacao.status, para, perfil.id);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Transição recusada.');
    }
  }

  const linhasComparativo: OrigemLinha[] = itens
    .filter((i) => respostaDoItem[i.id])
    .map((i) => ({
      id: i.id,
      produtoId: i.produtoId,
      produtoNome: porId[i.produtoId]?.nome ?? 'produto removido',
      fornecedorId: cotacao!.fornecedorId,
      fornecedorNome: fornecedor?.nomeFantasia || fornecedor?.razaoSocial || 'fornecedor removido',
      unidade: porId[i.produtoId]?.unidadeMedida ?? null,
      quantidadeDesejadaMilli: i.quantidadeDesejadaMilli,
      resposta: respostaDoItem[i.id]!,
      caminho: `/cotacoes/${cotacao!.id}/produto/${i.produtoId}`,
    }));

  return (
    <div className="flex flex-col gap-5">
      <Cabecalho
        titulo={nova ? 'Nova cotação' : (cotacao?.referencia ?? '')}
        descricao={
          nova
            ? 'Escolha o fornecedor e adicione os produtos que você quer cotar.'
            : `Registro do que ${fornecedor?.nomeFantasia || fornecedor?.razaoSocial || 'o fornecedor'} respondeu.`
        }
        acoes={
          <Link
            to="/cotacoes"
            className="inline-flex items-center gap-1.5 rounded border border-regua-2 px-3 py-1.5 text-sm hover:border-marca"
          >
            <ArrowLeft className="size-4" aria-hidden />
            Voltar
          </Link>
        }
      />

      {erro && <Erro titulo="Não deu para concluir" detalhe={erro} />}

      {!nova && cotacao && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-regua-2 bg-superficie px-4 py-3">
          <SeloCotacao status={efetivo} />
          {efetivo === 'EXPIRADA' && cotacao.status !== 'EXPIRADA' && (
            <span className="text-xs text-tinta-3">
              (gravada como {ROTULO_STATUS[cotacao.status]} — expirou pela validade, não por
              gravação)
            </span>
          )}
          <span className="text-xs text-tinta-2">
            responsável: {nomesUsuarios[cotacao.responsavelId] ?? '—'}
          </span>

          {podeEscrever && proximos.length > 0 && (
            <div className="ml-auto flex flex-wrap gap-1.5">
              {proximos.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => mudarStatus(p)}
                  className="rounded border border-regua-2 px-2.5 py-1 text-xs font-medium hover:border-marca hover:text-marca"
                >
                  {ROTULO_STATUS[p]}
                </button>
              ))}
            </div>
          )}
          {podeEscrever && proximos.length === 0 && (
            <span className="ml-auto text-xs text-tinta-3">
              status final — não há transição a partir daqui
            </span>
          )}
        </div>
      )}

      <form onSubmit={salvar} className="flex flex-col gap-4 rounded-md border border-regua-2 bg-superficie p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Campo rotulo="Fornecedor">
            <select
              value={rascunho.fornecedorId}
              onChange={(e) => editar('fornecedorId', e.target.value)}
              disabled={!podeEscrever || !nova}
              className="w-full rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca disabled:opacity-60"
            >
              <option value="">Escolha…</option>
              {fornecedores
                .filter((f) => f.ativo)
                .map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nomeFantasia || f.razaoSocial}
                  </option>
                ))}
            </select>
          </Campo>

          <Campo
            rotulo="Validade da proposta"
            dica="Depois desta data a cotação aparece como expirada, sozinha."
          >
            <input
              type="date"
              value={rascunho.validade ?? ''}
              onChange={(e) => editar('validade', e.target.value || null)}
              disabled={!podeEscrever}
              className="w-full rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca disabled:opacity-60"
            />
          </Campo>

          <Campo rotulo="Canal">
            <select
              value={rascunho.canal ?? ''}
              onChange={(e) => editar('canal', (e.target.value || null) as RascunhoCotacao['canal'])}
              disabled={!podeEscrever}
              className="w-full rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca disabled:opacity-60"
            >
              <option value="">Não informado</option>
              {(['EMAIL', 'WHATSAPP', 'TELEFONE', 'PRESENCIAL', 'OUTRO'] as const).map((c) => (
                <option key={c} value={c}>
                  {c.charAt(0) + c.slice(1).toLowerCase()}
                </option>
              ))}
            </select>
          </Campo>

          <CampoTexto
            rotulo="Observações"
            valor={rascunho.observacoes}
            aoMudar={(v) => editar('observacoes', v)}
            desabilitado={!podeEscrever}
          />
        </div>

        {podeEscrever && (
          <div>
            <button
              type="submit"
              disabled={salvando}
              className="rounded bg-marca px-4 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90 disabled:opacity-60"
            >
              {salvando ? 'Salvando…' : nova ? 'Criar cotação' : 'Salvar'}
            </button>
          </div>
        )}
      </form>

      {nova ? (
        <p className="text-sm text-tinta-3">
          Os produtos e as respostas entram depois que a cotação existir — ela precisa de um número
          antes de ter itens.
        </p>
      ) : (
        <>
          <Itens
            cotacaoId={cotacao!.id}
            fornecedorId={cotacao!.fornecedorId}
            statusAtual={cotacao!.status}
            itens={itens}
            respostas={respostaDoItem}
            produtos={produtos}
            podeEscrever={podeEscrever}
            usuarioId={perfil?.id ?? ''}
          />

          <section className="flex flex-col gap-2">
            <h2 className="flex items-center gap-2 text-base font-semibold">
              <BarChart3 className="size-4 text-marca" aria-hidden />
              Comparativo desta cotação
            </h2>
            {linhasComparativo.length === 0 ? (
              <p className="text-sm text-tinta-3">
                Nenhum item respondido ainda. O comparativo aparece quando houver ao menos uma
                resposta.
              </p>
            ) : (
              <>
                <TabelaComparativo origens={linhasComparativo} />
                <p className="text-xs text-tinta-3">
                  Clique no nome do produto para comparar este fornecedor com todos os outros que
                  cotaram o mesmo item.
                </p>
              </>
            )}
          </section>

          {historico.length > 0 && (
            <section className="flex flex-col gap-2">
              <h2 className="text-base font-semibold">Histórico de status</h2>
              <ul className="flex flex-col gap-1 rounded-md border border-regua-2 bg-superficie p-3 text-sm">
                {historico.map((h) => (
                  <li key={h.id} className="flex flex-wrap items-center gap-2 text-tinta-2">
                    <span className="num text-xs text-tinta-3">
                      {h.em?.toDate ? h.em.toDate().toLocaleString('pt-BR') : '—'}
                    </span>
                    <span>
                      {h.de ? `${ROTULO_STATUS[h.de]} → ` : ''}
                      <strong className="text-tinta">{ROTULO_STATUS[h.para]}</strong>
                    </span>
                    <span className="text-xs text-tinta-3">
                      por {nomesUsuarios[h.porUsuarioId] ?? '—'}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}

// ---------- itens e respostas ----------

function Itens({
  cotacaoId,
  fornecedorId,
  statusAtual,
  itens,
  respostas,
  produtos,
  podeEscrever,
  usuarioId,
}: {
  cotacaoId: string;
  fornecedorId: string;
  statusAtual: StatusCotacao;
  itens: ItemCotacao[];
  respostas: Record<string, RespostaCotacao>;
  produtos: { id: string; nome: string; unidadeMedida: string }[];
  podeEscrever: boolean;
  usuarioId: string;
}) {
  const [produtoId, setProdutoId] = useState('');
  const [quantidade, setQuantidade] = useState<number | null>(null);
  const [aberto, setAberto] = useState<string | null>(null);

  const jaNaCotacao = new Set(itens.map((i) => i.produtoId));
  const porId = Object.fromEntries(produtos.map((p) => [p.id, p]));

  async function adicionar() {
    if (!produtoId || !quantidade || quantidade <= 0) return;
    await adicionarItem(cotacaoId, produtoId, quantidade);
    setProdutoId('');
    setQuantidade(null);
  }

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-base font-semibold">Itens e respostas</h2>

      {podeEscrever && (
        <div className="flex flex-wrap items-end gap-2 rounded-md border border-regua-2 bg-superficie p-3">
          <div className="min-w-56 flex-1">
            <Campo rotulo="Produto">
              <select
                value={produtoId}
                onChange={(e) => setProdutoId(e.target.value)}
                className="w-full rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca"
              >
                <option value="">Escolha…</option>
                {produtos
                  .filter((p) => !jaNaCotacao.has(p.id))
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nome}
                    </option>
                  ))}
              </select>
            </Campo>
          </div>
          <div className="w-40">
            <CampoQuantidade
              rotulo="Quantidade desejada"
              milli={quantidade}
              aoMudar={setQuantidade}
            />
          </div>
          <button
            type="button"
            onClick={adicionar}
            disabled={!produtoId || !quantidade}
            className="inline-flex items-center gap-1.5 rounded bg-marca px-3 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90 disabled:opacity-50"
          >
            <Plus className="size-4" aria-hidden />
            Adicionar item
          </button>
        </div>
      )}

      {itens.length === 0 ? (
        <Vazio
          titulo="Nenhum item nesta cotação"
          descricao="Adicione os produtos que você pediu ao fornecedor, com a quantidade que pretende comprar. A quantidade desejada é o que faz a conta do custo efetivo sair certa."
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {itens.map((item) => {
            const produto = porId[item.produtoId];
            const resposta = respostas[item.id];
            const estaAberto = aberto === item.id;

            return (
              <li key={item.id} className="rounded-md border border-regua-2 bg-superficie">
                <div className="flex flex-wrap items-center gap-3 px-3 py-2">
                  <span className="font-medium">{produto?.nome ?? 'produto removido'}</span>
                  <span className="text-xs text-tinta-3">
                    quer <Quantidade milli={item.quantidadeDesejadaMilli} unidade={produto?.unidadeMedida} />
                  </span>
                  {resposta ? (
                    <span className="rounded bg-c-via/12 px-1.5 py-0.5 text-[0.65rem] font-semibold text-c-via">
                      RESPONDIDO
                    </span>
                  ) : (
                    <span className="rounded bg-tinta-3/12 px-1.5 py-0.5 text-[0.65rem] font-semibold text-tinta-3">
                      SEM RESPOSTA
                    </span>
                  )}

                  <div className="ml-auto flex gap-1.5">
                    <Link
                      to={`/cotacoes/${cotacaoId}/produto/${item.produtoId}`}
                      className="rounded border border-regua-2 px-2.5 py-1 text-xs hover:border-marca hover:text-marca"
                    >
                      Comparar fornecedores
                    </Link>
                    {podeEscrever && (
                      <>
                        <button
                          type="button"
                          onClick={() => setAberto(estaAberto ? null : item.id)}
                          className="rounded border border-regua-2 px-2.5 py-1 text-xs hover:border-marca hover:text-marca"
                        >
                          {estaAberto ? 'Fechar' : resposta ? 'Editar resposta' : 'Registrar resposta'}
                        </button>
                        <button
                          type="button"
                          onClick={() => removerItem(cotacaoId, item.id)}
                          aria-label={`Remover ${produto?.nome ?? 'item'}`}
                          className="rounded border border-regua-2 px-2 py-1 text-tinta-3 hover:border-pend hover:text-pend"
                        >
                          <Trash2 className="size-3.5" aria-hidden />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {estaAberto && (
                  <FormularioResposta
                    cotacaoId={cotacaoId}
                    item={item}
                    fornecedorId={fornecedorId}
                    resposta={resposta}
                    totalItens={itens.length}
                    statusAtual={statusAtual}
                    usuarioId={usuarioId}
                    aoConcluir={() => setAberto(null)}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

const RESPOSTA_VAZIA: DadosResposta = {
  precoUnitarioCents: null,
  quantidadeMinimaMilli: null,
  quantidadeDisponivelMilli: null,
  freteCents: null,
  impostosCents: null,
  prazoEntregaDias: null,
  condicaoPagamento: null,
  validade: null,
  dataResposta: null,
  requerRevisao: false,
};

/**
 * Formulario da resposta do fornecedor.
 *
 * Campo em branco grava NULO, nunca zero (RN-044). E por isso que nao existe
 * valor inicial "0,00" em lugar nenhum aqui: um frete vazio significa "o
 * fornecedor nao disse", e o comparativo precisa poder dizer isso.
 */
function FormularioResposta({
  cotacaoId,
  item,
  fornecedorId,
  resposta,
  totalItens,
  statusAtual,
  usuarioId,
  aoConcluir,
}: {
  cotacaoId: string;
  item: ItemCotacao;
  fornecedorId: string;
  resposta: RespostaCotacao | undefined;
  totalItens: number;
  statusAtual: StatusCotacao;
  usuarioId: string;
  aoConcluir: () => void;
}) {
  const [dados, setDados] = useState<DadosResposta>(() =>
    resposta
      ? {
          precoUnitarioCents: resposta.precoUnitarioCents,
          quantidadeMinimaMilli: resposta.quantidadeMinimaMilli,
          quantidadeDisponivelMilli: resposta.quantidadeDisponivelMilli,
          freteCents: resposta.freteCents,
          impostosCents: resposta.impostosCents,
          prazoEntregaDias: resposta.prazoEntregaDias,
          condicaoPagamento: resposta.condicaoPagamento,
          validade: resposta.validade,
          dataResposta: resposta.dataResposta,
          requerRevisao: resposta.requerRevisao,
        }
      : { ...RESPOSTA_VAZIA, dataResposta: hojeISO() },
  );
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const editar = <K extends keyof DadosResposta>(campo: K, valor: DadosResposta[K]) =>
    setDados((d) => ({ ...d, [campo]: valor }));

  async function gravar() {
    setErro(null);
    setSalvando(true);
    try {
      await salvarResposta(
        cotacaoId,
        { id: item.id, produtoId: item.produtoId },
        fornecedorId,
        dados,
        usuarioId,
        { totalItens, statusAtual },
      );
      aoConcluir();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível gravar a resposta.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="flex flex-col gap-3 border-t border-regua px-3 py-3">
      {erro && <Erro titulo="Resposta não gravada" detalhe={erro} />}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <CampoReais
          rotulo="Preço unitário"
          cents={dados.precoUnitarioCents}
          aoMudar={(v) => editar('precoUnitarioCents', v)}
        />
        <CampoQuantidade
          rotulo="Quantidade mínima"
          milli={dados.quantidadeMinimaMilli}
          aoMudar={(v) => editar('quantidadeMinimaMilli', v)}
          dica="Se for maior que a desejada, a conta usa esta."
        />
        <CampoQuantidade
          rotulo="Quantidade disponível"
          milli={dados.quantidadeDisponivelMilli}
          aoMudar={(v) => editar('quantidadeDisponivelMilli', v)}
        />
        <CampoReais
          rotulo="Frete"
          cents={dados.freteCents}
          aoMudar={(v) => editar('freteCents', v)}
          dica="Em branco = não informado, não é zero."
        />
        <CampoReais
          rotulo="Impostos"
          cents={dados.impostosCents}
          aoMudar={(v) => editar('impostosCents', v)}
          dica="Em branco = não informado, não é zero."
        />
        <CampoInteiro
          rotulo="Prazo de entrega"
          valor={dados.prazoEntregaDias}
          aoMudar={(v) => editar('prazoEntregaDias', v)}
          sufixo="dias"
        />
        <CampoTexto
          rotulo="Condição de pagamento"
          valor={dados.condicaoPagamento}
          aoMudar={(v) => editar('condicaoPagamento', v)}
          placeholder="30 dias, à vista…"
        />
        <Campo rotulo="Validade da resposta">
          <input
            type="date"
            value={dados.validade ?? ''}
            onChange={(e) => editar('validade', e.target.value || null)}
            className="w-full rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca"
          />
        </Campo>
        <Campo rotulo="Data da resposta">
          <input
            type="date"
            value={dados.dataResposta ?? ''}
            onChange={(e) => editar('dataResposta', e.target.value || null)}
            className="w-full rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca"
          />
        </Campo>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={dados.requerRevisao}
          onChange={(e) => editar('requerRevisao', e.target.checked)}
        />
        <span>
          Requer revisão
          <span className="ml-1 text-xs text-tinta-3">
            (algo neste dado precisa de conferência antes de virar análise)
          </span>
        </span>
      </label>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={gravar}
          disabled={salvando}
          className="rounded bg-marca px-4 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90 disabled:opacity-60"
        >
          {salvando ? 'Gravando…' : 'Gravar resposta'}
        </button>
        <button
          type="button"
          onClick={aoConcluir}
          className="rounded border border-regua-2 px-4 py-1.5 text-sm hover:border-marca"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}
